import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Bell,
  Settings,
  Mail,
  Clock,
  RefreshCw,
  Send,
  Users,
  Filter,
  AlertTriangle,
  Briefcase,
  AlertOctagon,
  Activity,
  GitBranch,
  Building2,
  CheckCircle,
  XCircle,
  ChevronRight,
  Calendar,
  Zap,
  FileText,
  Eye
} from 'lucide-react';

// Iconos por categoría
const CATEGORY_ICONS = {
  alertas: AlertTriangle,
  casos: Briefcase,
  escalaciones: AlertOctagon,
  monitores: Activity,
  aprobaciones: GitBranch,
  onboarding: Building2
};

// Colores por categoría
const CATEGORY_BG = {
  alertas: 'bg-amber-50 border-amber-200',
  casos: 'bg-indigo-50 border-indigo-200',
  escalaciones: 'bg-red-50 border-red-200',
  monitores: 'bg-purple-50 border-purple-200',
  aprobaciones: 'bg-blue-50 border-blue-200',
  onboarding: 'bg-green-50 border-green-200'
};

const NotificacionesAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  const [notificationLog, setNotificationLog] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('all');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    loadConfig();
    loadLog();
    loadUsers();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/notifications/v2/config');
      setConfig(res.data);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const loadLog = async () => {
    try {
      const res = await api.get('/notifications/v2/admin/log?limit=50');
      setNotificationLog(res.data || []);
    } catch (error) {
      console.error('Error loading log:', error);
    }
  };

  const loadAllNotifications = async (userId = null) => {
    try {
      const params = new URLSearchParams();
      if (userId && userId !== 'all') params.append('user_filter', userId);
      params.append('limit', '100');
      
      const res = await api.get(`/notifications/v2/admin/all?${params.toString()}`);
      setAllNotifications(res.data?.notifications || []);
    } catch (error) {
      console.error('Error loading all notifications:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get('/notifications/v2/admin/users');
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleTypeToggle = async (typeId, enabled, channels) => {
    setSaving(true);
    try {
      await api.put(`/notifications/v2/config/type/${typeId}`, {
        enabled,
        channels
      });
      await loadConfig();
      toast.success('Configuración actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleChannelToggle = (typeId, channel, currentChannels, enabled) => {
    const newChannels = enabled
      ? [...currentChannels, channel]
      : currentChannels.filter(c => c !== channel);
    
    const typeEnabled = config.enabled_types[typeId]?.enabled ?? true;
    handleTypeToggle(typeId, typeEnabled, newChannels);
  };

  const handleDigestUpdate = async (updates) => {
    setSaving(true);
    try {
      await api.put('/notifications/v2/config/digest', {
        ...config.digest,
        ...updates
      });
      await loadConfig();
      toast.success('Configuración del digest actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleImmediateEmailUpdate = async (updates) => {
    setSaving(true);
    try {
      await api.put('/notifications/v2/config/immediate-email', {
        enabled: config.immediate_email?.enabled ?? true,
        priority_threshold: config.immediate_email?.priority_threshold ?? 'high',
        cooldown_minutes: config.immediate_email?.cooldown_minutes ?? 5,
        quiet_hours_enabled: config.immediate_email?.quiet_hours?.enabled ?? false,
        quiet_hours_start: config.immediate_email?.quiet_hours?.start ?? '22:00',
        quiet_hours_end: config.immediate_email?.quiet_hours?.end ?? '08:00',
        ...updates
      });
      await loadConfig();
      toast.success('Configuración actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const res = await api.post('/notifications/v2/test-email');
      if (res.data.success) {
        toast.success('Email de prueba enviado');
      } else {
        toast.error('Error al enviar email');
      }
    } catch (error) {
      toast.error('Error al enviar prueba');
    } finally {
      setSendingTest(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  // Agrupar tipos por categoría
  const typesByCategory = {};
  Object.entries(config?.notification_types || {}).forEach(([typeId, typeInfo]) => {
    const category = typeInfo.category || 'sistema';
    if (!typesByCategory[category]) {
      typesByCategory[category] = [];
    }
    typesByCategory[category].push({ id: typeId, ...typeInfo });
  });

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="text-indigo-600" />
            Administración de Notificaciones
          </h1>
          <p className="text-slate-600 mt-1">
            Configura los canales y tipos de notificación del sistema
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/m/notificaciones')}
          >
            <Bell size={16} className="mr-2" />
            Mi Bandeja
          </Button>
          <Button
            variant="outline"
            onClick={handleSendTestEmail}
            disabled={sendingTest}
          >
            <Send size={16} className="mr-2" />
            {sendingTest ? 'Enviando...' : 'Probar Email'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings size={14} />
            Configuración
          </TabsTrigger>
          <TabsTrigger value="digest" className="flex items-center gap-2">
            <FileText size={14} />
            Digest Diario
          </TabsTrigger>
          <TabsTrigger value="log" className="flex items-center gap-2">
            <Clock size={14} />
            Historial
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2" onClick={() => loadAllNotifications()}>
            <Eye size={14} />
            Ver Todas
          </TabsTrigger>
        </TabsList>

        {/* Tab: Configuración de Tipos */}
        <TabsContent value="config">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tipos de Notificación por Categoría */}
            <div className="lg:col-span-2 space-y-6">
              {Object.entries(typesByCategory).map(([category, types]) => {
                const CategoryIcon = CATEGORY_ICONS[category] || Bell;
                const categoryBg = CATEGORY_BG[category] || 'bg-slate-50 border-slate-200';
                const categoryInfo = config?.categories?.[category] || { name: category };
                
                return (
                  <Card key={category} className="bg-white">
                    <CardHeader className={`${categoryBg} border-b rounded-t-lg`}>
                      <CardTitle className="text-base flex items-center gap-2">
                        <CategoryIcon size={18} />
                        {categoryInfo.name || category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 divide-y divide-slate-100">
                      {types.map(type => {
                        const typeConfig = config?.enabled_types?.[type.id] || {};
                        const channels = typeConfig.channels || type.default_channels || [];
                        const isEnabled = typeConfig.enabled !== false;
                        
                        return (
                          <div key={type.id} className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-slate-900">{type.name}</span>
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {type.priority}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-500">{type.description}</p>
                                
                                {/* Canales */}
                                <div className="flex items-center gap-4 mt-3">
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id={`${type.id}-inapp`}
                                      checked={channels.includes('in_app')}
                                      onChange={(e) => handleChannelToggle(type.id, 'in_app', channels, e.target.checked)}
                                      className="rounded border-slate-300"
                                      disabled={!isEnabled}
                                    />
                                    <Label htmlFor={`${type.id}-inapp`} className="text-xs text-slate-600 cursor-pointer">
                                      In-App
                                    </Label>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id={`${type.id}-email`}
                                      checked={channels.includes('email')}
                                      onChange={(e) => handleChannelToggle(type.id, 'email', channels, e.target.checked)}
                                      className="rounded border-slate-300"
                                      disabled={!isEnabled}
                                    />
                                    <Label htmlFor={`${type.id}-email`} className="text-xs text-slate-600 cursor-pointer">
                                      Email
                                    </Label>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id={`${type.id}-digest`}
                                      checked={channels.includes('digest')}
                                      onChange={(e) => handleChannelToggle(type.id, 'digest', channels, e.target.checked)}
                                      className="rounded border-slate-300"
                                      disabled={!isEnabled}
                                    />
                                    <Label htmlFor={`${type.id}-digest`} className="text-xs text-slate-600 cursor-pointer">
                                      Digest
                                    </Label>
                                  </div>
                                </div>
                              </div>
                              
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={(v) => handleTypeToggle(type.id, v, channels)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Panel lateral - Email inmediato */}
            <div className="space-y-6">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="text-amber-500" size={18} />
                    Email Inmediato
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Habilitado</Label>
                    <Switch
                      checked={config?.immediate_email?.enabled ?? true}
                      onCheckedChange={(v) => handleImmediateEmailUpdate({ enabled: v })}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-500">Umbral de prioridad</Label>
                    <Select
                      value={config?.immediate_email?.priority_threshold || 'high'}
                      onValueChange={(v) => handleImmediateEmailUpdate({ priority_threshold: v })}
                    >
                      <SelectTrigger className="mt-1" style={{ backgroundColor: '#fff' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Baja (todas)</SelectItem>
                        <SelectItem value="medium">Media</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="critical">Crítica (solo urgentes)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400 mt-1">
                      Solo se envían emails inmediatos para notificaciones con esta prioridad o mayor
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500">Cooldown (minutos)</Label>
                    <Input
                      type="number"
                      value={config?.immediate_email?.cooldown_minutes || 5}
                      onChange={(e) => handleImmediateEmailUpdate({ cooldown_minutes: parseInt(e.target.value) })}
                      min={1}
                      max={60}
                      className="mt-1"
                      style={{ backgroundColor: '#fff' }}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Tiempo mínimo entre emails del mismo tipo
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                <CardContent className="pt-4">
                  <h4 className="font-medium text-indigo-900 mb-2">💡 Consejo</h4>
                  <p className="text-sm text-indigo-800">
                    Configura el <strong>umbral de prioridad</strong> en "Alta" para recibir emails solo de eventos importantes, 
                    y usa el <strong>Digest diario</strong> para el resto.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Tab: Digest Diario */}
        <TabsContent value="digest">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar size={18} className="text-indigo-600" />
                  Configuración del Digest
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Digest habilitado</Label>
                  <Switch
                    checked={config?.digest?.enabled ?? true}
                    onCheckedChange={(v) => handleDigestUpdate({ enabled: v })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Hora de envío</Label>
                    <Input
                      type="number"
                      value={config?.digest?.schedule_hour || 8}
                      onChange={(e) => handleDigestUpdate({ schedule_hour: parseInt(e.target.value) })}
                      min={0}
                      max={23}
                      className="mt-1"
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Minutos</Label>
                    <Input
                      type="number"
                      value={config?.digest?.schedule_minute || 0}
                      onChange={(e) => handleDigestUpdate({ schedule_minute: parseInt(e.target.value) })}
                      min={0}
                      max={59}
                      className="mt-1"
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Días de envío</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'mon', label: 'Lun' },
                      { id: 'tue', label: 'Mar' },
                      { id: 'wed', label: 'Mié' },
                      { id: 'thu', label: 'Jue' },
                      { id: 'fri', label: 'Vie' },
                      { id: 'sat', label: 'Sáb' },
                      { id: 'sun', label: 'Dom' }
                    ].map(day => (
                      <Button
                        key={day.id}
                        variant={config?.digest?.days?.includes(day.id) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          const currentDays = config?.digest?.days || [];
                          const newDays = currentDays.includes(day.id)
                            ? currentDays.filter(d => d !== day.id)
                            : [...currentDays, day.id];
                          handleDigestUpdate({ days: newDays });
                        }}
                        style={config?.digest?.days?.includes(day.id) ? { backgroundColor: '#4f46e5' } : {}}
                      >
                        {day.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText size={18} className="text-indigo-600" />
                  Secciones del Digest
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { id: 'pending_alerts', label: 'Alertas Pendientes', icon: AlertTriangle },
                  { id: 'pending_approvals', label: 'Aprobaciones Pendientes', icon: GitBranch },
                  { id: 'active_escalations', label: 'Escalaciones Activas', icon: AlertOctagon },
                  { id: 'assigned_cases', label: 'Casos Asignados', icon: Briefcase },
                  { id: 'activity_summary', label: 'Resumen de Actividad', icon: Activity }
                ].map(section => {
                  const Icon = section.icon;
                  const isIncluded = config?.digest?.include_sections?.includes(section.id);
                  
                  return (
                    <div
                      key={section.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isIncluded ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} className={isIncluded ? 'text-indigo-600' : 'text-slate-400'} />
                        <span className={isIncluded ? 'text-indigo-900' : 'text-slate-600'}>
                          {section.label}
                        </span>
                      </div>
                      <Switch
                        checked={isIncluded}
                        onCheckedChange={(v) => {
                          const current = config?.digest?.include_sections || [];
                          const updated = v
                            ? [...current, section.id]
                            : current.filter(s => s !== section.id);
                          handleDigestUpdate({ include_sections: updated });
                        }}
                      />
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Historial */}
        <TabsContent value="log">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock size={18} />
                Historial de Envíos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {notificationLog.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No hay registros</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {notificationLog.map((log, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{log.type}</Badge>
                          <Badge className={
                            log.priority === 'critical' ? 'bg-red-100 text-red-700' :
                            log.priority === 'high' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {log.priority === 'critical' ? 'Crítica' : log.priority === 'high' ? 'Alta' : log.priority === 'medium' ? 'Media' : 'Baja'}
                          </Badge>
                        </div>
                        <span className="text-xs text-slate-400">
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-800 mb-1">{log.title}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>{log.recipients?.length || 0} destinatarios</span>
                        <span className="flex items-center gap-1">
                          {log.channels_used?.includes('email') && (
                            <Mail size={12} className="text-blue-500" />
                          )}
                          {log.channels_used?.includes('in_app') && (
                            <Bell size={12} className="text-purple-500" />
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Ver Todas */}
        <TabsContent value="all">
          <Card className="bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye size={18} />
                  Todas las Notificaciones
                </CardTitle>
                <Select value={userFilter} onValueChange={(v) => {
                  setUserFilter(v);
                  loadAllNotifications(v);
                }}>
                  <SelectTrigger className="w-[200px]" style={{ backgroundColor: '#fff' }}>
                    <SelectValue placeholder="Filtrar por usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los usuarios</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.nombre || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {allNotifications.length === 0 ? (
                <p className="text-center text-slate-400 py-8">
                  Selecciona un usuario o haz clic en "Ver Todas" para cargar
                </p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {allNotifications.map((n, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${n.read ? 'bg-white' : 'bg-blue-50'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{n.user_name}</span>
                          {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500"></span>}
                        </div>
                        <span className="text-xs text-slate-400">{formatDate(n.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{n.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NotificacionesAdmin;
